import React from 'react';
import { Formik, yupToFormErrors } from 'formik';
import { apiPost } from 'helpers/ApiHelper';
import Auth from 'j-toker';
import styled from 'styled-components';
import Spacer from 'components/ui/Spacer';
import TextBox from 'components/ui/TextBox';
import DatePicker from 'components/ui/DatePicker';
import Select from 'components/ui/SelectField';
import Form from 'components/ui/Form';
import Row from 'components/ui/Row';
import Col from 'components/ui/Col';
import { VerticalFormWrapper } from 'components/ui/VerticalFormWrapper';
import * as Yup from 'yup';
import jstz from 'jstz';
import { getTranslatedString } from 'helpers/i18n';
import { toFriendlyDateString, onlyNumbers } from 'helpers/Formatter';
import NumberFormat from 'react-number-format';

const FormTitle = styled.span`
  font-size: 20px;
  font-family: 'Circular-Bold';
`;

const FullWidthInput = styled(TextBox)`
  width: 100%;
`;
const FullWidthSelect = styled(Select)`
  width: 100%;
`;

const FullWidthDatePicker = styled(DatePicker)`
  width: 100%;
`;

const paymentTypes = [
  {
    value: getTranslatedString('payment_type.amenity_fee'),
    label: getTranslatedString('payment_type.amenity_fee'),
  },
  {
    value: getTranslatedString('payment_type.ancillary_charge'),
    label: getTranslatedString('payment_type.ancillary_charge'),
  },
  {
    value: getTranslatedString('payment_type.deposit'),
    label: getTranslatedString('payment_type.deposit'),
  },
  {
    value: getTranslatedString('payment_type.late_fee'),
    label: getTranslatedString('payment_type.late_fee'),
  },
  {
    value: getTranslatedString('payment_type.monthly_rent'),
    label: getTranslatedString('payment_type.monthly_rent'),
  },
  {
    value: getTranslatedString('payment_type.nsf_fee'),
    label: getTranslatedString('payment_type.nsf_fee'),
  },
];

export default class CreatePaymentDrawerDetailsStep extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      selectedItem: undefined,
      birthdayMode: 'year',
      loadingUnits: false,
      loadingLeases: false,
    };
  }

  componentDidMount() {
    const timezone = jstz.determine();

    this.setState({
      timezone: timezone.name() || 'America/Toronto',
    });
  }

  handlePropertyChange = async (value, setFieldValue) => {
    setFieldValue('property_id', value);
    setFieldValue('unit_id', undefined);
    this.setState({ loadingUnits: true });
    await this.props.listUnits(value);
    this.setState({ loadingUnits: false });
  };

  handleUnitChange = async (value, setFieldValue) => {
    this.setState({ loadingLeases: true });
    setFieldValue('unit_id', value);
    setFieldValue('lease_id', undefined);
    await this.props.fetchLeases(value);
    this.setState({ loadingLeases: false });
  };

  handleFormSubmit = (values) => {
    const payload = {
      ...values,
      payment_amount: onlyNumbers(values.payment_amount),
    };
    this.props.onLoadingChange({ isLoading: true, values: payload });
    this.createPaymentUrl(payload)
      .done((response) => {
        this.props.onSubmissionComplete(payload);
      })
      .catch((error) => {
        this.props.onLoadingChange({ isError: true, isLoading: false, values: payload });
      });
  };

  createPaymentUrl = (payload) => {
    return apiPost(Auth.getApiUrl() + '/landlords/payment', payload);
  };

  validateForm = async (values) => {
    const requiredErrorVerbiage = getTranslatedString('required');

    let validationSchema = {
      payment_amount: Yup.string().required(requiredErrorVerbiage),
      property_id: Yup.string().required(requiredErrorVerbiage),
      payment_type: Yup.string().required(requiredErrorVerbiage),
      due_date: Yup.date().required(requiredErrorVerbiage),
      lease_id: Yup.string().required(requiredErrorVerbiage),
      unit_id: Yup.string().required(requiredErrorVerbiage),
    };

    const schema = Yup.object().shape(validationSchema);
    return schema.validate(values, { abortEarly: false }).catch((error) => {
      throw yupToFormErrors(error);
    });
  };

  render() {
    const { searchActivated } = this.state;
    const { formData } = this.props;

    return (
      <React.Fragment>
        <Formik
          initialValues={{
            property_id: formData.property_id,
            unit_id: formData.unit_id,
            lease_id: formData.lease_id,
            payment_amount: '',
            payment_type: undefined,
            due_date: undefined,
          }}
          onSubmit={(values) => this.handleFormSubmit(values)}
          validate={this.validateForm}
        >
          {({ values, errors, touched, handleChange, setFieldValue, handleSubmit }) => (
            <VerticalFormWrapper id={this.props.formName} onSubmit={handleSubmit}>
              {this.renderPropertyInformation(values, errors, touched, handleChange, setFieldValue)}
              {!searchActivated &&
                this.renderCreatePayment(values, errors, touched, handleChange, setFieldValue)}
            </VerticalFormWrapper>
          )}
        </Formik>
      </React.Fragment>
    );
  }

  renderCreateNewPaymentHeader = () => (
    <Spacer mb={1}>
      <FormTitle>{getTranslatedString('landlordPortal.charges.chargeInformation')}</FormTitle>
    </Spacer>
  );

  renderCreatePayment = (values, errors, touched, handleChange, setFieldValue) => {
    return (
      <React.Fragment>
        {this.renderCreateNewPaymentHeader()}
        <Spacer mb={1}>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                label={`${getTranslatedString('payments.dueDate')}*`}
                validateStatus={touched.due_date && errors.due_date ? 'error' : null}
                help={touched.due_date && errors.due_date}
              >
                <FullWidthDatePicker
                  name="due_date"
                  value={values.due_date}
                  onChange={(value) => setFieldValue('due_date', value, true)}
                  placeholder="e.g. 2020-01-01"
                  mobileMode="date"
                  size="large"
                  id="error"
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                label={`${getTranslatedString('payments.paymentType')}*`}
                validateStatus={touched.payment_type && errors.payment_type ? 'error' : null}
                help={touched.payment_type && errors.payment_type}
              >
                <FullWidthSelect
                  name="payment_type"
                  showSearch
                  placeholder="Select"
                  size="large"
                  value={values.payment_type}
                  onChange={(value) => setFieldValue('payment_type', value)}
                  filterOption={(input, option) =>
                    option.props.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
                  }
                >
                  {paymentTypes.map((paymentType) => (
                    <Select.Option key={paymentType} value={paymentType.value}>
                      {paymentType.label}
                    </Select.Option>
                  ))}
                </FullWidthSelect>
              </Form.Item>
            </Col>
          </Row>
        </Spacer>
        <Spacer mb={1}>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                label={`${getTranslatedString('landlordPortal.charges.chargeAmount')}*`}
                validateStatus={touched.payment_amount && errors.payment_amount ? 'error' : null}
                help={touched.payment_amount && errors.payment_amount}
              >
                <NumberFormat
                  value={values.payment_amount}
                  thousandSeparator={true}
                  prefix="$"
                  customInput={FullWidthInput}
                  name="payment_amount"
                  onChange={handleChange}
                  placeholder="$1,000"
                  size="large"
                />
              </Form.Item>
            </Col>
          </Row>
        </Spacer>
      </React.Fragment>
    );
  };

  renderPropertyInformation = (values, errors, touched, handleChange, setFieldValue) => {
    return (
      <React.Fragment>
        <Spacer mb={1}>
          <FormTitle>{getTranslatedString('payments.propertyInformation')}</FormTitle>
        </Spacer>
        <Spacer mb={1}>
          <Row gutter={16}>
            <Col span={12}>{this.renderPropertyField(values, setFieldValue, touched, errors)}</Col>
            <Col span={12}>{this.renderUnitsField(values, setFieldValue, touched, errors)}</Col>
          </Row>
        </Spacer>
        <Spacer mb={1}>
          <Row gutter={16}>
            <Col span={24}>
              {this.renderCurrentLeasesField(values, setFieldValue, touched, errors)}
            </Col>
          </Row>
        </Spacer>
      </React.Fragment>
    );
  };

  renderUnitsField = (values, setFieldValue, touched, errors) => {
    const { units } = this.props;
    const { loadingUnits } = this.state;

    return (
      <Form.Item
        label={`${getTranslatedString('selectAUnit')}*`}
        validateStatus={touched.unit_id && errors.unit_id ? 'error' : null}
        help={touched.unit_id && errors.unit_id}
      >
        <FullWidthSelect
          name="unit_id"
          showSearch
          placeholder="Select"
          size="large"
          value={values.unit_id}
          disabled={loadingUnits || units.length === 0}
          loading={loadingUnits}
          onChange={(value) => this.handleUnitChange(value, setFieldValue)}
          filterOption={(input, option) =>
            option.props.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
          }
        >
          {units.map((unit) => (
            <Select.Option key={unit} value={unit.value}>
              {unit.label}
            </Select.Option>
          ))}
        </FullWidthSelect>
      </Form.Item>
    );
  };

  renderCurrentLeasesField = (values, setFieldValue, touched, errors) => {
    const { currentLeasesOfUnit } = this.props;
    const { loadingLeases } = this.state;

    return (
      <Form.Item
        label={`${getTranslatedString('payments.selectLease')}*`}
        validateStatus={touched.lease_id && errors.lease_id ? 'error' : null}
        help={touched.lease_id && errors.lease_id}
      >
        <FullWidthSelect
          name="lease_id"
          showSearch
          placeholder="Select"
          size="large"
          value={values.lease_id}
          disabled={loadingLeases || currentLeasesOfUnit.length === 0}
          loading={loadingLeases}
          onChange={(value) => setFieldValue('lease_id', value)}
          filterOption={(input, option) =>
            option.props.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
          }
        >
          {currentLeasesOfUnit.map((lease) => (
            <Select.Option key={lease} value={lease.id}>
              {getTranslatedString('payments.leaseEnding', {
                cotenantNames: lease.cotenants,
                leaseEndDate: toFriendlyDateString(lease.lease_end_date),
              })}
              {lease.cancelled && ` ${getTranslatedString('payments.cancelled')}`}
            </Select.Option>
          ))}
        </FullWidthSelect>
      </Form.Item>
    );
  };

  renderPropertyField = (values, setFieldValue, touched, errors) => {
    const { properties } = this.props;

    return (
      <Form.Item
        label={`${getTranslatedString('showingDrawer.selectAProperty')}*`}
        validateStatus={touched.property_id && errors.property_id ? 'error' : null}
        help={touched.property_id && errors.property_id}
      >
        <FullWidthSelect
          name="property_id"
          showSearch
          placeholder="Select"
          size="large"
          value={values.property_id}
          onChange={(value) => this.handlePropertyChange(value, setFieldValue)}
          filterOption={(input, option) =>
            option.props.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
          }
        >
          {properties.map((property) => (
            <Select.Option value={property.value} key={property.value}>
              {property.label}
            </Select.Option>
          ))}
        </FullWidthSelect>
      </Form.Item>
    );
  };
}
